import { defineStore } from 'pinia';
import { ref } from 'vue';
import { OrgContactService } from '@/services/orgContact.services';
import type {
  IOrganisationListContact,
  ICreateOrgContactBody,
  IUpdateOrgContactBody,
} from '@/types/manageOrgCommerce.types';

/**
 * Store Pinia pour la gestion des contacts d'une organisation.
 */
export const useOrgContactStore = defineStore('orgContact', () => {
  const contacts = ref<IOrganisationListContact[]>([]);
  const currentContact = ref<IOrganisationListContact | null>(null);

  /**
   * Crée un contact pour une organisation.
   * @param organizationId - UUID de l'organisation
   * @param data - Corps de la requête
   */
  const createOrgContact = async (
    organizationId: string,
    data: ICreateOrgContactBody,
  ): Promise<IOrganisationListContact> => {
    try {
      const response = await OrgContactService.createOrgContact(
        organizationId,
        data,
      );
      const contact = (response.data as any)?.data ?? response.data;
      contacts.value.push(contact);
      return contact;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Liste les contacts d'une organisation.
   * @param organizationId - UUID de l'organisation
   */
  const searchOrgContacts = async (
    organizationId: string,
  ): Promise<IOrganisationListContact[]> => {
    try {
      const response =
        await OrgContactService.searchOrgContacts(organizationId);
      const list: IOrganisationListContact[] = Array.isArray(response.data)
        ? response.data
        : ((response.data as any)?.data ?? []);
      contacts.value = list;
      return list;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Récupère un contact par son ID.
   * @param organizationId - UUID de l'organisation
   * @param contactId - UUID du contact
   */
  const getOrgContactById = async (
    organizationId: string,
    contactId: string,
  ): Promise<IOrganisationListContact> => {
    try {
      const response = await OrgContactService.getOrgContactById(
        organizationId,
        contactId,
      );
      currentContact.value = response.data;
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Met à jour un contact d'organisation.
   * @param organizationId - UUID de l'organisation
   * @param contactId - UUID du contact
   * @param data - Corps de la requête
   */
  const updateOrgContact = async (
    organizationId: string,
    contactId: string,
    data: IUpdateOrgContactBody,
  ): Promise<IOrganisationListContact> => {
    try {
      const response = await OrgContactService.updateOrgContact(
        organizationId,
        contactId,
        data,
      );
      const updated = (response.data as any)?.data ?? response.data;
      const idx = contacts.value.findIndex((c) => c.id === contactId);
      if (idx !== -1) contacts.value[idx] = updated;
      return updated;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Supprime un contact d'organisation.
   * @param organizationId - UUID de l'organisation
   * @param contactId - UUID du contact
   */
  const deleteOrgContact = async (
    organizationId: string,
    contactId: string,
  ): Promise<void> => {
    try {
      await OrgContactService.deleteOrgContact(organizationId, contactId);
      contacts.value = contacts.value.filter((c) => c.id !== contactId);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  return {
    contacts,
    currentContact,
    createOrgContact,
    searchOrgContacts,
    getOrgContactById,
    updateOrgContact,
    deleteOrgContact,
  };
});
